import type { ListInstanceHandle } from 'src/models/listInstance';

import { ref, shallowReactive } from 'vue';

// shallowReactive: das Map ist reaktiv (set/delete triggern), die Handles selbst müssen es nicht sein.
const handles = shallowReactive(new Map<string, ListInstanceHandle>());
// Task 3.1 (Logout-/Deaktivierungs-Teardown): dispose-Callback pro Handle, separat von `handles` gehalten
// (kein reaktives Tracking nötig) — der Lifecycle-Composable (useListInstanceLifecycle) ist weiterhin
// alleiniger Besitzer der Dispose-Logik (Slice-Cleanup, Store-Dispose, State-Slot-Löschung, Query-Store-
// Teardown); die Registry speichert nur einen Zeiger darauf, damit App-weiter Teardown (z. B. der
// 'logout'-EventBus-Handler, der AUSSERHALB jeder Komponenten-Setup-Funktion läuft) ihn ohne Re-Implementierung
// aufrufen kann.
const disposers = new Map<string, () => void>();
const activeKey = ref<null | string>(null);

export const listInstanceRegistry = {
	clear(): void {
		handles.clear();
		disposers.clear();
		activeKey.value = null;
	},
	// Teardown-Einstiegspunkt für Nicht-Unmount-Pfade (Logout — siehe Befund in useListInstanceLifecycle.ts:
	// Logout ist ein harter window.location.href-Reload, Vue unmountet dabei NICHT geordnet, onUnmounted
	// feuert also nicht zuverlässig). Snapshot vor dem Iterieren: jeder dispose()-Callback ruft intern
	// unregister() auf und mutiert `handles`/`disposers` — direktes Iterieren der live Map wäre UB.
	disposeAll(): void {
		Array.from(disposers.values()).forEach((dispose) => dispose());
		this.clear();
	},
	entries(): Array<ListInstanceHandle> {
		return Array.from(handles.values());
	},
	get(genericListIdentifier: string): ListInstanceHandle | null {
		return handles.get(genericListIdentifier) ?? null;
	},
	getActive(): ListInstanceHandle | null {
		if (activeKey.value === null) {
			return null;
		}
		return handles.get(activeKey.value) ?? null;
	},
	register(handle: ListInstanceHandle, dispose?: () => void): void {
		handles.set(handle.genericListIdentifier, handle);
		if (dispose !== undefined) {
			disposers.set(handle.genericListIdentifier, dispose);
		}
	},
	setActive(genericListIdentifier: null | string): void {
		activeKey.value = genericListIdentifier;
	},
	unregister(genericListIdentifier: string): void {
		handles.delete(genericListIdentifier);
		disposers.delete(genericListIdentifier);
		if (activeKey.value === genericListIdentifier) {
			activeKey.value = null;
		}
	},
};
